import { useEffect, useState } from "react"
import { Download, Share, SquarePlus, X } from "lucide-react"
import { Button } from "@/components/ui/button"

/**
 * Ajakan pasang aplikasi ke layar utama.
 *
 * Dua jalur yang berbeda mendasar:
 *  - Chrome/Edge/Android: browser mengirim `beforeinstallprompt`, jadi kita bisa
 *    menahannya lalu memanggil `prompt()` sendiri lewat tombol.
 *  - iOS Safari: TIDAK punya event itu sama sekali dan tidak menyediakan API
 *    apa pun untuk memicu pemasangan. Satu-satunya cara adalah pengguna menekan
 *    Bagikan → "Add to Home Screen". Karena itu di iOS yang kita tampilkan
 *    bukan tombol, melainkan petunjuk langkahnya.
 */

const DISMISS_KEY = "pwa-install-dismissed"

/** Event Chrome; tidak ada di lib DOM standar. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>
}

function isIOS(): boolean {
  if (typeof navigator === "undefined") return false
  const ua = navigator.userAgent
  // iPadOS 13+ menyamar sebagai Mac — dikenali dari layar sentuhnya.
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
}

/** Sudah berjalan sebagai aplikasi terpasang? Jangan ganggu kalau iya. */
function isStandalone(): boolean {
  if (typeof window === "undefined") return false
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // Properti khusus Safari iOS, tidak ada di tipe Navigator standar.
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

export function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)
  const [show, setShow] = useState(false)
  const [ios, setIos] = useState(false)

  useEffect(() => {
    if (isStandalone()) return

    // `?install=1` memaksa panel muncul — dipakai untuk mencoba tampilannya di
    // localhost atau di iOS, tanpa menunggu syarat heuristik browser terpenuhi.
    const paksa = new URLSearchParams(window.location.search).get("install") === "1"
    if (!paksa && localStorage.getItem(DISMISS_KEY) === "1") return

    const diIOS = isIOS()
    setIos(diIOS)

    // iOS tidak akan pernah mengirim beforeinstallprompt → langsung tampilkan
    // petunjuk manual. Diberi jeda supaya tidak menimpa halaman saat dibuka.
    if (diIOS) {
      const t = setTimeout(() => setShow(true), paksa ? 0 : 3000)
      return () => clearTimeout(t)
    }

    if (paksa) setShow(true)

    const onPrompt = (e: Event) => {
      e.preventDefault() // tahan mini-infobar bawaan, kita tampilkan versi sendiri
      setDeferred(e as BeforeInstallPromptEvent)
      setShow(true)
    }
    window.addEventListener("beforeinstallprompt", onPrompt)

    const onInstalled = () => {
      setShow(false)
      localStorage.setItem(DISMISS_KEY, "1")
    }
    window.addEventListener("appinstalled", onInstalled)

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt)
      window.removeEventListener("appinstalled", onInstalled)
    }
  }, [])

  function tutup() {
    setShow(false)
    localStorage.setItem(DISMISS_KEY, "1")
  }

  async function pasang() {
    if (!deferred) return
    await deferred.prompt()
    const { outcome } = await deferred.userChoice
    if (outcome === "accepted") localStorage.setItem(DISMISS_KEY, "1")
    setDeferred(null)
    setShow(false)
  }

  if (!show) return null

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 mx-auto w-full max-w-[480px] p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
      <div className="relative rounded-2xl border bg-card p-4 shadow-lg">
        <button
          onClick={tutup}
          aria-label="Tutup ajakan pasang aplikasi"
          className="absolute right-2 top-2 rounded-lg p-1.5 text-muted-foreground active:opacity-70"
        >
          <X size={16} />
        </button>

        <div className="flex items-start gap-3">
          <img
            src="/images/beternak-ayam-logo.png"
            alt=""
            width={40}
            height={40}
            className="mt-0.5 shrink-0 rounded-xl"
          />
          <div className="min-w-0 pr-6">
            <p className="font-black leading-tight">Pasang di layar utama</p>
            <p className="mt-0.5 text-sm text-muted-foreground leading-snug">
              Buka lebih cepat, tanpa bilah alamat, dan tetap bisa dibuka saat sinyal lemah.
            </p>

            {ios ? (
              // iOS: tidak ada tombol pasang — Safari tidak menyediakannya.
              <ol className="mt-3 space-y-1.5 text-sm">
                <li className="flex items-center gap-2">
                  <span className="font-bold">1.</span>
                  Ketuk <Share size={15} className="inline text-primary" /> di bilah bawah Safari
                </li>
                <li className="flex items-center gap-2">
                  <span className="font-bold">2.</span>
                  Pilih <SquarePlus size={15} className="inline text-primary" /> "Add to Home Screen"
                </li>
                <li className="flex items-center gap-2">
                  <span className="font-bold">3.</span>
                  Ketuk "Add" di pojok kanan atas
                </li>
              </ol>
            ) : (
              <Button onClick={pasang} disabled={!deferred} className="mt-3 w-full">
                <Download size={16} /> Pasang Sekarang
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
